import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ArrowLeftIcon, SendIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input, InputPassword } from '@/components/ui/input';

const beanstalkFormSchema = z.object({
  accountName: z.string().min(1, 'Enter a name for this integration'),
  username: z.string().min(1, 'Enter your Beanstalk username'),
  password: z.string().min(1, 'Enter your Beanstalk password'),
});

export type IBeanstalkForm = z.infer<typeof beanstalkFormSchema>;

type Props = {
  onSubmit: (data: IBeanstalkForm) => Promise<void>;
  onBack: () => void;
  accountName?: string;
  btnText?: React.ReactNode;
};

const BeanstalkForm = ({ onSubmit, onBack, accountName = '', btnText = 'Submit' }: Props) => {
  const form = useForm<IBeanstalkForm>({
    resolver: zodResolver(beanstalkFormSchema),
    defaultValues: {
      accountName,
      username: '',
      password: '',
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="accountName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Integration name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g., My Beanstalk Org name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="your-username" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <InputPassword {...field} placeholder="Enter your password here" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <Button size="sm" variant="outline" className="sm:flex-1" onClick={onBack}>
            <ArrowLeftIcon /> View instructions
          </Button>
          <Button
            type="submit"
            size="sm"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            className="sm:flex-1"
          >
            {btnText} <SendIcon />
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BeanstalkForm;
